import App from './application-container';
import { animate } from './scene';
import { LOG, display_message_to_user } from '@phi/logging/frontend';
import * as THREE from 'three';
import { show_modal, hide_modal } from './modals';
import { get_global_id } from './JCore/jcore';
import { WrappedValue } from '@phi/wrapped-value';
import { withSpinner } from './state/spinner';
import { request_setting_button_states, suppress_undo_tracking } from './utility';
import { getImage } from '@phi/frontend/api/calls';
import { uid_load_model } from './input_output';
import LoadedSketchElement from './LoadedSketchElement';
import { uil_get_edges_of_face, uil_get_face_geometry, uil_get_world_scaling_factor, uil_is_object_visible, uil_move_vertex_to_coords } from './ui_logic';
import { find_all_pairs_in_unlistified_edge_list, mustNotDisplayFace } from './lattice';
import { set_default_vertex_color } from './colours_materials_appearance';
import { uid_calculate_global_bounding_box, uid_deselect_all_objects, uid_hide_rigid_move_manipulator } from './ui_display';
import { make_moving_coordinates_invisible, make_selected_coordinates_invisible } from '../../../../src/features/indicate-vertices';
import { load_texture, read_as_data_url } from '@phi/saving-loading';

export function display_sketch_images_modal() {
  show_modal("sketchImagesModal");
}

export const load_sketch = withSpinner('Loading sketch file', (currently_processed_sketch, width, height, orientation) => new Promise(async (yes) => {
  hide_modal('sketchImagesModal');

  if (!currently_processed_sketch) {
    display_message_to_user(LOG.WARN, "Sketch not selected");
    return;
  }

  // Read dimenstions and placement orientation from modal...
  const img_width = width ?? 0;
  const img_height = height ?? 0;
  const img_orientation = orientation;

  const img = await getImage(
    currently_processed_sketch.documentId,
    currently_processed_sketch.elementId,
    currently_processed_sketch.workspaceId,
    currently_processed_sketch.versionId
  );

  const loaded_sketch_element = new LoadedSketchElement();
  loaded_sketch_element.elementId = currently_processed_sketch.elementId;
  loaded_sketch_element.documentId = currently_processed_sketch.documentId;
  loaded_sketch_element.versionId = currently_processed_sketch.versionId;
  loaded_sketch_element.workspaceId = currently_processed_sketch.workspaceId;
  loaded_sketch_element.filename = currently_processed_sketch.filename;

  var loader = new THREE.TextureLoader();
  loader.setCrossOrigin("");
  loader.load(
    img,
    (texture) => {
      loaded_sketch_element.texture = texture;
      yes(complete_load_sketch(loaded_sketch_element, img_width, img_height, img_orientation));
    },
    undefined,
    err => {
      display_message_to_user(LOG.WARN, 'This is not a recognized file type. Please try with a different file. Recognized formats include .jpg, .png and .gif');
      yes(err);
    }
  );
}));

/**
    @param {File} file
    @param {number} width
    @param {number} height
    @param {'xz' | 'yz' | 'xy'} orientation
    @returns {Promise<void | Error>}
*/
export async function load_local_sketch(file, width, height, orientation) {
    const loaded_sketch_element = new LoadedSketchElement();
    const data_url = await read_as_data_url(file);
    if (data_url instanceof Error) {
        display_message_to_user(LOG.WARN, data_url.message);
        return data_url;
    }

    const texture = await load_texture(data_url);
    if (texture instanceof Error) {
        display_message_to_user(LOG.WARN, data_url.message);
        return texture;
    }

    loaded_sketch_element.texture = texture;
    complete_load_sketch(loaded_sketch_element, width, height, orientation);
}

export async function complete_load_sketch(loaded_sketch_element, width, height, orientation) {
  suppress_undo_tracking();

  var img_original_width = loaded_sketch_element.texture.image.width;
  var img_original_height = loaded_sketch_element.texture.image.height;
  if (width == 0 && height !=0) {
    width = height * (img_original_width/img_original_height);
  }
  else if (width != 0 && height == 0) {
    height = width * (img_original_height/img_original_width);
  }
  else if (width == 0 && height == 0) {
    // If it's an empty scene, set a world scaling factor of 1.
    if (uil_get_world_scaling_factor() == 0)
      uil_set_world_scaling_factor(1.0);
    var bb = uid_calculate_global_bounding_box(true, false, false);
    var dim = Math.max(bb.dimension, 1.0);
    width = get_display_unit_from_unitless_value(dim * 1.1);
    height = width * (img_original_height/img_original_width);
  }

  var img_id = "img_" + get_global_id();
  loaded_sketch_element.image_id = img_id;

  // Based on the orientation load and position a square body
  var previous_body_list = uil_get_model_bodies();

  // Remember what the previous model units were, and apply them again after load.
  var previous_model_units = App.state.display_units;

  window.loading_into_current_model = true;

  const square = await fetch('/models/square.phi').then(x => x.text());
  await uid_load_model(square, false, false, true, true);

  // Find ID of loaded body, then the image face and vertices
  var new_body_list = uil_get_model_bodies();
  var img_body_id;
  if (previous_body_list == null || previous_body_list.length == 0)
    img_body_id = new_body_list[0];
  else {
    new_body_list.forEach(function(bid){
      if (previous_body_list && previous_body_list.indexOf(bid) == -1)
        img_body_id = bid;
    });
  }
  console.log(img_body_id);

  var img_face_id = 0;
  uid_faces.keys().forEach(function(fid){
    if (uil_get_body_of_face(fid) == img_body_id && uil_is_object_visible(fid))
      img_face_id = fid;
  });

  loaded_sketch_element.body_id = img_body_id;
  loaded_sketch_element.face_id = img_face_id;

  // Position vertices of face according to image requirements.

  // Get the vertices in order
  // Create list with all edge pairs in the listified edge array.
  var edge_pairs = find_all_pairs_in_unlistified_edge_list(uil_get_edges_of_face(img_face_id));
  var vs = [];
  edge_pairs.forEach(function(ep){
    let v = uil_find_edge_shared_vertex(ep[0], ep[1]);
    vs.push(v);
  });

  var center = new THREE.Vector3();
  var wv_radius = new WrappedValue(0);
  var wv_flatness = new WrappedValue(0);
  var characteristic_dir_coords = new THREE.Vector3();
  var normal = new THREE.Vector3();
  uil_get_face_geometry(img_face_id, center, normal, wv_radius, characteristic_dir_coords, wv_flatness);
  normal.normalize();

  // Check whether we should invert the order of the vertices
  var v0 = new THREE.Vector3();
  var v1 = new THREE.Vector3();
  var v2 = new THREE.Vector3();
  v0.subVectors(uid_vertices.getItem(vs[0]).object3d.position, center);
  v1.subVectors(uid_vertices.getItem(vs[1]).object3d.position, center);
  v2.cross(v1, v0);
  if (v2.normal < 0) {
    // Must flip vertex order
    var vtmp = new THREE.Vector3();
    vtmp = vs[0];
    vs[0] = vs[3];
    vs[3] = vtmp;
    vtmp = vs[1];
    vs[1] = vs[2];
    vs[2] = vtmp;
  }

  // Revert to the previous workspace units and set the dimensions
  App.state.display_units = previous_model_units;
  var w = get_unitless_value_from_display_unit(width);
  var h = get_unitless_value_from_display_unit(height);

  if (orientation != "xy" && orientation != "yz" && orientation != "xz")
    orientation = "xy";

  if (orientation == "xy") {
    // Position the 4 vertices as appropriate
    var topLeftV = vs[3];
    var topRightV = vs[2];
    var bottomRightV = vs[1];
    var bottomLeftV = vs[0];

    uil_move_vertex_to_coords( topLeftV,     (-1) * w / 2.0, h / 2.0, 0 );
    uil_move_vertex_to_coords( topRightV,    w / 2.0,        h / 2.0, 0 );
    uil_move_vertex_to_coords( bottomRightV, w / 2.0,        (-1) * h / 2.0,  0 );
    uil_move_vertex_to_coords( bottomLeftV,  (-1) * w / 2.0, (-1) * h / 2.0,  0 );
  } else if (orientation == "yz") {
    // Position the 4 vertices as appropriate
    var topLeftV = vs[3];
    var topRightV = vs[2];
    var bottomRightV = vs[1];
    var bottomLeftV = vs[0];

    uil_move_vertex_to_coords( topLeftV,     0, (-1) * w / 2.0, h / 2.0 );
    uil_move_vertex_to_coords( topRightV,    0, w / 2.0,        h / 2.0 );
    uil_move_vertex_to_coords( bottomRightV, 0, w / 2.0,        (-1) * h / 2.0 );
    uil_move_vertex_to_coords( bottomLeftV,  0, (-1) * w / 2.0, (-1) * h / 2.0 );
  } else if (orientation == "xz") {
    // Position the 4 vertices as appropriate
    var topLeftV = vs[3];
    var topRightV = vs[2];
    var bottomRightV = vs[1];
    var bottomLeftV = vs[0];

    uil_move_vertex_to_coords( topLeftV,     (-1) * w / 2.0, 0, h / 2.0 );
    uil_move_vertex_to_coords( topRightV,    w / 2.0,        0, h / 2.0 );
    uil_move_vertex_to_coords( bottomRightV, w / 2.0,        0, (-1) * h / 2.0 );
    uil_move_vertex_to_coords( bottomLeftV,  (-1) * w / 2.0, 0, (-1) * h / 2.0 );
  }

  loaded_sketch_element.topLeftV = topLeftV;
  loaded_sketch_element.topRightV = topRightV;
  loaded_sketch_element.bottomRightV = bottomRightV;
  loaded_sketch_element.bottomLeftV = bottomLeftV;

  App.state.imported_sketches.push(loaded_sketch_element);
  attach_sketch_to_face(loaded_sketch_element);
  move_sketch_objects_out_of_objects_raynode();
  show_sketches();

  unsuppress_undo_tracking_if_ready(false);
  animate();
  request_setting_button_states();
}

export function move_sketch_objects_out_of_objects_raynode() {
  // Get all new objects out of uid_objects_raynode and into the proper ray node:
  for (var i = uid_objects_raynode.children.length - 1; i >= 0; i--) {
    var m = uid_objects_raynode.children[i];
    var uid_obj = uid_object3dIDToUid_object.getItem(m.id);
    if (uid_obj) {
      if (uid_obj.type == "face" && face_is_for_sketch(uid_obj.id)) {
        current_scene_raynode_for_sketch_objects().add(m);
      }
      else if (uid_obj.type == "edge" && edge_is_for_sketch(uid_obj.id)) {
        current_scene_raynode_for_sketch_objects().add(m);
      }
      else if (uid_obj.type == "vertex" && vertex_is_for_sketch(uid_obj.id)) {
        current_scene_raynode_for_sketch_objects().add(m);
      }
    }
  }

  // And re-draw everything.
  uid_edges.keys().forEach(function(eid){
    set_default_edge_color(eid);
  });
  uid_faces.keys().forEach(function(fid){
    set_default_face_color(fid);
  });
  uid_vertices.keys().forEach(function(vid){
    set_default_vertex_color(vid);
  });
}

export function attach_sketch_to_face(loaded_sketch_element) {

  // Check that objects exist.
  if (!isFace(loaded_sketch_element.face_id) ||
      !isVertex(loaded_sketch_element.topLeftV) || !isVertex(loaded_sketch_element.topRightV) ||
      !isVertex(loaded_sketch_element.bottomLeftV) || !isVertex(loaded_sketch_element.bottomRightV)) {
    display_message_to_user(LOG.WARN, "Unable to attach sketch. Please load it again from your Onshape document");
    App.state.imported_sketches.splice(App.state.imported_sketches.indexOf(loaded_sketch_element), 1);
    return;
  }

  // Attach image to face
  var p0 = new THREE.Vector3();
  var p1 = new THREE.Vector3();
  var p2 = new THREE.Vector3();
  var p3 = new THREE.Vector3();

  p0.copy(uid_vertices.getItem(loaded_sketch_element.topLeftV).object3d.position);
  p1.copy(uid_vertices.getItem(loaded_sketch_element.topRightV).object3d.position);
  p2.copy(uid_vertices.getItem(loaded_sketch_element.bottomRightV).object3d.position);
  p3.copy(uid_vertices.getItem(loaded_sketch_element.bottomLeftV).object3d.position);

  var center = new THREE.Vector3();
  var wv_radius = new WrappedValue(0);
  var wv_flatness = new WrappedValue(0);
  var characteristic_dir_coords = new THREE.Vector3();
  var normal = new THREE.Vector3();
  uil_get_face_geometry(loaded_sketch_element.face_id, center, normal, wv_radius, characteristic_dir_coords, wv_flatness);
  normal.normalize();
  normal.multiplyScalar(uid_vertex_diameter/20.0);

  uil_set_face_material([loaded_sketch_element.face_id], "sketch_material");
  refresh_face_for_material(loaded_sketch_element.face_id, "sketch_material", false, false);

  p0.add(normal)
  p1.add(normal)
  p2.add(normal)
  p3.add(normal)

  var quad_vertices =
  [
    p3.x, p3.y, p3.z,
    p2.x, p2.y, p2.z,
    p1.x, p1.y, p1.z,
    p0.x, p0.y, p0.z
  ];

  /*
  var quad_vertices =
  [
      -3.0,  3.0, 0.0,
       3.0,  3.0, 0.0,
       3.0, -3.0, 0.0,
      -3.0, -3.0, 0.0
  ];
  */


  var quad_uvs =
  [
  0.0, 0.0,
  1.0, 0.0,
  1.0, 1.0,
  0.0, 1.0
  ];

  var quad_indices =
  [
  0, 2, 1, 0, 3, 2
  ];

  var geometry = new THREE.BufferGeometry();

  var vertices = new Float32Array( quad_vertices );
  // Each vertex has one uv coordinate for texture mapping
  var uvs = new Float32Array( quad_uvs);
  // Use the four vertices to draw the two triangles that make up the square.
  var indices = new Uint32Array( quad_indices )

  // itemSize = 3 because there are 3 values (components) per vertex
  geometry.addAttribute( 'position', new THREE.BufferAttribute( vertices, 3 ) );
  geometry.addAttribute( 'uv', new THREE.BufferAttribute( uvs, 2 ) );
  geometry.setIndex( new THREE.BufferAttribute( indices, 1 ) );

  var material = new THREE.MeshBasicMaterial( { map: loaded_sketch_element.texture } );

  var mesh = new THREE.Mesh( geometry, material );

  mesh.DoubleSided = true;
  mesh.material.side = THREE.DoubleSide;
  mesh.material.transparent = true;
  mesh.material.opacity = 0.6;
  mesh.material.depthTest = true;
  mesh.material.depthWrite = true;
  mesh.name = "sketchImage-" + loaded_sketch_element.image_id;
  uid_sketch_plane_raynode.add(mesh);
  loaded_sketch_element.mesh = mesh;
  animate();
}

export function face_is_for_sketch(fid) {
  if (!isFace(fid))
    return false;
  return ( /*!(mustNotDisplayFace(fid)) &&*/ body_is_for_sketch(uil_get_body_of_face(fid)));
}

export function edge_is_for_sketch(eid) {
  return (/*!(mustNotDisplayEdge(eid)) &&*/ body_is_for_sketch(uil_get_body_of_edge(eid)));
}

export function vertex_is_for_sketch(vid) {
  return (/*!(mustNotDisplayFace(vid)) &&*/ body_is_for_sketch(uil_get_body_of_vertex(vid)));
}

export function body_is_for_sketch(bid) {
  var it_is = false;
  App.state.imported_sketches.forEach(function(loaded_sketch_element) {
    if (!loaded_sketch_element.is_deleted && loaded_sketch_element.body_id == bid)
      it_is = true;
  });
  return it_is;
}

export function non_deleted_reference_sketches() {
  return App.state.imported_sketches.filter(s => !s.is_deleted);
}

export function number_of_non_deleted_reference_sketches() {
  return non_deleted_reference_sketches().length;
}

export function redraw_sketches() {
  var loaded_sketch_elements_to_display = [];

  App.state.imported_sketches.forEach(function(loaded_sketch_element) {
    if (!mustNotDisplayFace(loaded_sketch_element.face_id))
      loaded_sketch_elements_to_display.push(loaded_sketch_element);
  });

  for (var i = uid_sketch_plane_raynode.children.length - 1; i >= 0; i--) {
    var m = uid_sketch_plane_raynode.children[i];
    uid_sketch_plane_raynode.remove(m);
    m.geometry.dispose();
    m.material.dispose();
  }

  loaded_sketch_elements_to_display.forEach(function(loaded_sketch_element) {
    attach_sketch_to_face(loaded_sketch_element);
  });

  animate();
}

export function delete_sketch_of_face(fid) {
  var image_id;

  for (var i = App.state.imported_sketches.length - 1; i >= 0; i--) {
    var loaded_sketch_element = App.state.imported_sketches[i];
    if (loaded_sketch_element.face_id == fid) {
      image_id = loaded_sketch_element.image_id;
      App.state.imported_sketches[i].is_deleted = true;
    }
  }

  for (var i = uid_sketch_plane_raynode.children.length - 1; i >= 0; i--) {
    var m = uid_sketch_plane_raynode.children[i];
    if (m.name.includes(image_id)) {
      uid_deleted_sketch_plane_raynode.add(m);
      //uid_sketch_plane_raynode.remove(m);
      //m.geometry.dispose();
      //m.material.dispose();
    }
  }

  animate();

}

export function undelete_sketch_of_face(fid) {
  var image_id;

  for (var i = App.state.imported_sketches.length - 1; i >= 0; i--) {
    var loaded_sketch_element = App.state.imported_sketches[i];
    if (loaded_sketch_element.face_id == fid) {
      image_id = loaded_sketch_element.image_id;
      App.state.imported_sketches[i].is_deleted = false;
    }
  }

  for (var i = uid_deleted_sketch_plane_raynode.children.length - 1; i >= 0; i--) {
    var m = uid_deleted_sketch_plane_raynode.children[i];
    if (m.name.includes(image_id)) {
      uid_sketch_plane_raynode.add(m);
    }
  }
}

export function start_editing_sketches() {
  if (App.state.editing_reference_sketches)
    return;

  uid_deselect_all_objects();
  make_moving_coordinates_invisible();
  make_selected_coordinates_invisible();
  show_sketches();

  // First make sketches visible if they are not

  // Move everything out of uid_objects_raynode and into temporary node.
  // Then move everything from sketch node into uid_objects_raynode
  for (var i = uid_objects_raynode.children.length - 1; i >= 0; i--) {
    var n = uid_objects_raynode.children[i];
    uid_temporary_node_for_sketch_raynode.add(n);
  }
  for (var i = uid_sketch_objects_raynode.children.length - 1; i >= 0; i--) {
    var n = uid_sketch_objects_raynode.children[i];
    uid_objects_raynode.add(n);
  }
  App.state.editing_reference_sketches = true;

  // And re-draw edges & vertices to give them the right color
  uid_edges.keys().forEach(function(eid){
    set_default_edge_color(eid);
  });
  uid_vertices.keys().forEach(function(vid){
    set_default_vertex_color(vid);
  });

  request_setting_button_states();
  animate();
}

export function stop_editing_sketches() {
  if (!App.state.editing_reference_sketches)
    return;

  uid_hide_rigid_move_manipulator(false, 0, true);

  uid_deselect_all_objects();
  make_moving_coordinates_invisible();
  make_selected_coordinates_invisible();

  // Move everything out of uid_objects_raynode, and back into sketch node.
  // Move everything from tempoorary node into uid_objects_raynode.
  for (var i = uid_objects_raynode.children.length - 1; i >= 0; i--) {
    var n = uid_objects_raynode.children[i];
    uid_sketch_objects_raynode.add(n);
  }
  for (var i = uid_temporary_node_for_sketch_raynode.children.length - 1; i >= 0; i--) {
    var n = uid_temporary_node_for_sketch_raynode.children[i];
    uid_objects_raynode.add(n);
  }
  App.state.editing_reference_sketches = false;

  // And re-draw edges & vertices to give them the right color
  uid_edges.keys().forEach(function(eid){
    set_default_edge_color(eid);
  });
  uid_vertices.keys().forEach(function(vid){
    set_default_vertex_color(vid);
  });

  request_setting_button_states();
  animate();
}

export function move_reference_sketch_objects_to_objects_raynode() {

}

export function move_reference_sketch_objects_back_from_objects_raynode() {

}

export function current_scene_raynode_for_sketch_objects() {
  if (App.state.editing_reference_sketches)
    return uid_objects_raynode;
  else
    return uid_sketch_objects_raynode;
}

export function show_sketches() {
  if (number_of_non_deleted_reference_sketches == 0)
    return;

  if (App.state.showing_reference_sketches)
    return;


  App.state.showing_reference_sketches = true;

  var node_for_sketches;
  if (App.state.editing_reference_sketches)
    // Sketch objects reside under uid_objects_raynode
    node_for_sketches = uid_objects_raynode;
  else
    // Sketch objects reside under uid_sketch_objects_raynode
    node_for_sketches = uid_sketch_objects_raynode;

  // Redisplay all sketches
  for (var i = uid_hidden_sketch_objects_raynode.children.length - 1; i >= 0; i--) {
    var n = uid_hidden_sketch_objects_raynode.children[i];
    var obj = uid_object3dIDToUid_object.getItem(n.id);
    if (obj) {
      if (obj && (obj.type == "face" && uil_is_object_visible(obj.id)) || obj.type == "edge" || obj.type == "vertex")
        node_for_sketches.add(n);
      if (obj && obj.type == "edge")
        uid_visible_edge_mesh_raynode.add(uid_object3dIDToUid_object.getItem(n.id).visible_edge_mesh);
    }
  }
  for (var i = uid_hidden_sketch_plane_raynode.children.length - 1; i >= 0; i--) {
    var n = uid_hidden_sketch_plane_raynode.children[i];
    uid_sketch_plane_raynode.add(n);
  }

  uid_state.showing_sketches = true;

  animate();
  set_button_states();
}

export function hide_sketches() {
  if (App.state.imported_sketches.length == 0)
    return;

  if (!App.state.showing_reference_sketches)
    return;

  App.state.showing_reference_sketches = false;

  var node_for_sketches;
  if (App.state.editing_reference_sketches)
    // Sketch objects reside under uid_objects_raynode
    node_for_sketches = uid_objects_raynode;
  else
    // Sketch objects reside under uid_sketch_objects_raynode
    node_for_sketches = uid_sketch_objects_raynode;

  // There are sketches to be hidden
  for (var i = node_for_sketches.children.length - 1; i >= 0; i--) {
    var n = node_for_sketches.children[i];
    uid_hidden_sketch_objects_raynode.add(n);
    var obj = uid_object3dIDToUid_object.getItem(n.id);
    if (obj)
      uid_visible_edge_mesh_raynode.remove(obj.visible_edge_mesh);
  }
  for (var i = uid_sketch_plane_raynode.children.length - 1; i >= 0; i--) {
    var n = uid_sketch_plane_raynode.children[i];
    uid_hidden_sketch_plane_raynode.add(n);
  }

  uid_state.showing_sketches = false;

  if (App.state.editing_reference_sketches)
    stop_editing_sketches();

  animate();
  set_button_states();
}
